import { memo, useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { isExternalUrl, isSafeImageSrc, resolveDocumentUrl } from '../../markdown/security/sanitizeSchema';
import type { MarkdownImageProps } from '../../types';

// A styled element rather than `Box`, because Box would consume `width` and
// `height` as style props instead of emitting the HTML attributes that give the
// browser an intrinsic aspect ratio.
const Image = styled('img')(({ theme }) => ({
  display: 'block',
  maxWidth: '100%',
  height: 'auto',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

/**
 * Image with a graceful failure path.
 *
 * A blocked or broken source renders a labelled placeholder that keeps the alt
 * text visible, rather than a browser-default broken icon.
 */
export const MarkdownImage = memo(function MarkdownImage({
  src,
  alt = '',
  title,
  width,
  height,
  allowExternal = true,
  baseUrl,
}: MarkdownImageProps) {
  const theme = useTheme();
  const [failed, setFailed] = useState(false);

  // An image served from cache can finish before React attaches onError, so the
  // completed state is checked on mount as well.
  const ref = useCallback((node: HTMLImageElement | null) => {
    if (node?.complete && node.naturalWidth === 0) setFailed(true);
  }, []);

  const safe = isSafeImageSrc(src, baseUrl);
  const blocked = !safe || (!allowExternal && isExternalUrl(src, baseUrl));

  if (blocked || failed) {
    return (
      <Box
        component="span"
        role="img"
        aria-label={alt || 'Image unavailable'}
        data-testid="image-fallback"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          maxWidth: '100%',
          px: 1.5,
          py: 1.25,
          my: 0.5,
          borderRadius: `${theme.shape.borderRadius}px`,
          border: `1px dashed ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.text.primary, 0.03),
          color: theme.palette.text.secondary,
        }}
      >
        <BrokenImageOutlinedIcon fontSize="small" />
        <Typography variant="caption" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
          {blocked ? `Blocked image${alt ? `: ${alt}` : ''}` : alt || 'Image could not be loaded'}
        </Typography>
      </Box>
    );
  }

  const image = (
    <Image
      ref={ref}
      src={src ? resolveDocumentUrl(src, baseUrl) : undefined}
      alt={alt}
      width={width}
      height={height}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );

  // A title on an image is the Markdown convention for a caption.
  if (!title) return image;

  return (
    <Box component="span" role="figure" aria-label={title} className="markdown-image-figure" sx={{ display: 'inline-flex', flexDirection: 'column', maxWidth: '100%', my: 2.5 }}>
      {image}
      <Typography
        component="span"
        className="markdown-image-caption"
        variant="caption"
        sx={{ display: 'block', mt: 1, color: 'text.secondary', textAlign: 'center' }}
      >
        {title}
      </Typography>
    </Box>
  );
});

export default MarkdownImage;
